import { HqApiService } from 'hq/services/hqApiService';
import { OgpApiService } from 'ogp/services/ogp-api-service';
import { Site } from 'shared/hooks';
import { ShiftBulkArchivePayload } from 'hq/services/shift-planning-service.types';
import { BreakOption } from '@types';
import { PartnerApiService } from 'partner/shared/services/partner-api-service';
import { invariant } from 'shared/utils/utils';
import {
  BulkShiftActionData,
  bulkShiftActionDataSchema,
  getBreakOptionsSchema,
} from './schema/bulk-shift-action-data.schema';
import {
  BulkShiftActionDataResponseDto,
  CreateShiftRequestDto,
  DuplicateShiftRequestDto,
  GetShiftCreateDataResponseDto,
  PreferredWorkerOptionRequestDto,
  ShiftBulkEditPayloadDto,
  ShiftPlanningShiftsPartner,
  UpdateShiftRequestDto,
} from './shift-service.dto';
import {
  GetShiftCreateData,
  getShiftCreateDataSchema,
} from './schema/get-shift-create-data.schema';
import {
  ShiftDetail,
  shiftDetailSchema,
  ShiftPopoverDetail,
  shiftPopoverDetailSchema,
} from './schema/shift-detail.schema';
import { ShiftDetailDto, ShiftPopoverDetailDto } from './dto/shift-detail.dto';
import {
  PreferredWorkerOptions,
  preferredWorkerOptionsSchema,
} from './schema/preferred-worker-options.schema';
import { PreferredWorkerProposals } from './types/preferred-worker-proposals.types';
import {
  PreferredWorkerProposalsDto,
  PreferredWorkerProposalsRequestDto,
} from './dto/preferred-worker-proposals.dto';

export class ShiftService {
  constructor(
    private readonly ogpApiService: OgpApiService,
    private readonly hqApiService: HqApiService,
    private readonly partnerApiService: PartnerApiService
  ) {}

  public async getShiftCreateData(site: Site, companyId?: string): Promise<GetShiftCreateData> {
    const response = await this.getShiftCreateDataImpl(site, companyId);

    return getShiftCreateDataSchema.parse(response);
  }

  public async bulkUpdateShifts(
    site: Site,
    data: ShiftBulkEditPayloadDto
  ): Promise<BulkShiftActionData> {
    const response = await this.bulkUpdateShiftsImpl(site, data);

    return bulkShiftActionDataSchema.parse(response);
  }

  public async bulkArchiveShifts(
    site: Site,
    data: ShiftBulkArchivePayload
  ): Promise<BulkShiftActionData> {
    const response = await this.bulkArchiveShiftsImpl(site, data);

    return bulkShiftActionDataSchema.parse(response);
  }

  public async getBreakOptions(site: Site): Promise<{ breakOptions: BreakOption[] }> {
    const response = await this.getBreakOptionsImpl(site);
    return getBreakOptionsSchema.parse(response);
  }

  private async getBreakOptionsImpl(site: Site): Promise<{ breakOptions: BreakOption[] }> {
    if (site === 'hq') {
      return await this.hqApiService.get<{ breakOptions: BreakOption[] }>({
        path: 'break-options',
      });
    }
    if (site === 'ogp') {
      return await this.ogpApiService.get<{ breakOptions: BreakOption[] }>({
        path: 'shifts/get-break-options',
      });
    }

    if (site === 'partner') {
      return await this.partnerApiService.get<{ breakOptions: BreakOption[] }>({
        path: 'checkouts/break-options',
      });
    }
    throw new Error('Not implemented');
  }

  private async bulkUpdateShiftsImpl(
    site: Site,
    data: ShiftBulkEditPayloadDto
  ): Promise<BulkShiftActionDataResponseDto> {
    const path = `shifts/bulk-update`;

    if (site === 'hq') {
      return await this.hqApiService.update<BulkShiftActionDataResponseDto>({
        path,
        body: data,
      });
    }

    if (site === 'ogp') {
      return await this.ogpApiService.update<BulkShiftActionDataResponseDto>({
        path,
        body: data,
      });
    }

    throw new Error('Not implemented');
  }

  private async bulkArchiveShiftsImpl(
    site: Site,
    data: ShiftBulkArchivePayload
  ): Promise<BulkShiftActionDataResponseDto> {
    const path = `shifts/bulk-archive`;

    if (site === 'hq') {
      return await this.hqApiService.update<BulkShiftActionDataResponseDto>({
        path,
        body: data,
      });
    }

    if (site === 'ogp') {
      return await this.ogpApiService.update<BulkShiftActionDataResponseDto>({
        path,
        body: data,
      });
    }

    throw new Error('Not implemented');
  }

  private async getShiftCreateDataImpl(
    site: Site,
    companyId: string | undefined
  ): Promise<GetShiftCreateDataResponseDto> {
    const path = `shifts/get-shift-create-data`;
    if (site === 'hq') {
      return await this.hqApiService.get<GetShiftCreateDataResponseDto>({
        path,
        params: { companyId },
      });
    }

    if (site === 'ogp') {
      return await this.ogpApiService.get<GetShiftCreateDataResponseDto>({
        path,
      });
    }

    throw new Error('Not implemented');
  }

  public createShift = async (site: Site, shift: CreateShiftRequestDto) => {
    if (site === 'hq') {
      return await this.hqApiService.create<string[]>({
        path: `shifts`,
        body: shift,
      });
    } else if (site === 'ogp') {
      return await this.ogpApiService.create<string[]>({
        path: `shifts`,
        body: shift,
      });
    }

    throw new Error('Not implemented');
  };

  public updateShift = async (site: Site, shift: UpdateShiftRequestDto) => {
    if (site === 'hq') {
      return await this.hqApiService.update<void>({
        path: `shifts/${shift.id}`,
        body: shift,
      });
    } else if (site === 'ogp') {
      return await this.ogpApiService.update<void>({
        path: `shifts/${shift.id}`,
        body: shift,
      });
    }

    throw new Error('Not implemented');
  };

  public duplicateShift = async (site: Site, shift: DuplicateShiftRequestDto) => {
    if (site === 'hq') {
      return await this.hqApiService.create<string[]>({
        path: `shifts/duplicate`,
        body: shift,
      });
    } else if (site === 'ogp') {
      return await this.ogpApiService.create<string[]>({
        path: `shifts/duplicate`,
        body: shift,
      });
    }

    throw new Error('Not implemented');
  };

  public getPreferredWorkerOptions = async (
    site: Site,
    companyId?: string,
    departmentId?: string,
    jobTypeId?: string,
    certificates: string[] = [],
    isTempWork: boolean = false,
    isFreelance: boolean = false,
    startDateTime?: string,
    endDateTime?: string,
    isEmployee: boolean = false,
    flexPoolIds: string[] = [],
    flexPoolOnly: boolean = false,
    nameSearch?: string
  ): Promise<PreferredWorkerOptions> => {
    invariant(departmentId, 'Department ID is required');
    invariant(jobTypeId, 'Job Type ID is required');
    invariant(startDateTime, 'Start DateTime is required for preferred worker options');
    invariant(endDateTime, 'End DateTime is required for preferred worker options');

    const response = await this.getPreferredWorkerOptionsImpl(
      site,
      companyId,
      departmentId,
      jobTypeId,
      certificates,
      isTempWork,
      isFreelance,
      startDateTime,
      endDateTime,
      isEmployee,
      flexPoolIds,
      flexPoolOnly,
      nameSearch
    );
    return preferredWorkerOptionsSchema.parse(response);
  };

  private getPreferredWorkerOptionsImpl = async (
    site: Site,
    companyId: string | undefined,
    departmentId: string,
    jobTypeId: string,
    certificates: string[],
    isTempWork: boolean,
    isFreelance: boolean,
    startDateTime: string,
    endDateTime: string,
    isEmployee: boolean,
    flexPoolIds: string[],
    flexPoolOnly: boolean,
    nameSearch?: string
  ): Promise<PreferredWorkerOptions> => {
    const params: PreferredWorkerOptionRequestDto = {
      companyId,
      jobTypeId,
      departmentId,
      nameSearch,
      certificates,
      isTempWork,
      isFreelance,
      isEmployee,
      flexPoolOnly,
      startDateTime,
      endDateTime,
      ...(flexPoolIds.length > 0 ? { flexPoolIds: flexPoolIds.join(',') } : {}),
    };

    if (site === 'hq') {
      return await this.hqApiService.get<PreferredWorkerOptions>({
        path: `shifts/preferred-worker-options`,
        params,
      });
    } else if (site === 'ogp') {
      return await this.ogpApiService.get<PreferredWorkerOptions>({
        path: `shifts/preferred-worker-options`,
        params,
      });
    }

    throw new Error('Not implemented');
  };

  public getShiftDetail = async (site: Site, shiftId: string): Promise<ShiftDetail> => {
    const response = await this.getShiftDetailImpl(site, shiftId);
    return shiftDetailSchema.parse(response);
  };

  private async getShiftDetailImpl(site: Site, shiftId: string): Promise<ShiftDetailDto> {
    const path = `shifts/${shiftId}`;
    if (site === 'hq') {
      return await this.hqApiService.get<ShiftDetailDto>({
        path,
      });
    }
    if (site === 'ogp') {
      return await this.ogpApiService.get<ShiftDetailDto>({
        path,
      });
    }
    throw new Error('Not implemented');
  }

  public async getPreferredWorkerProposals(
    site: Site,
    params: PreferredWorkerProposalsRequestDto
  ): Promise<PreferredWorkerProposals> {
    return await this.getPreferredWorkerProposalsImpl(site, params);
  }

  public getShiftPopoverDetail = async (
    site: Site,
    shiftId: string
  ): Promise<ShiftPopoverDetail> => {
    const response = await this.getShiftPopoverDetailImpl(site, shiftId);
    return shiftPopoverDetailSchema.parse(response);
  };

  private async getShiftPopoverDetailImpl(
    site: Site,
    shiftId: string
  ): Promise<ShiftPopoverDetailDto> {
    const path = `shifts/${shiftId}/popover-detail`;
    if (site === 'ogp') {
      return await this.ogpApiService.get<ShiftPopoverDetailDto>({
        path,
      });
    }
    throw new Error('Not implemented');
  }

  //  There is no runtime validation by zod because we expect to have shared DTOs between the client and server soon
  private async getPreferredWorkerProposalsImpl(
    site: Site,
    params: PreferredWorkerProposalsRequestDto
  ): Promise<PreferredWorkerProposalsDto> {
    const path = `shift-planning/get-preferred-worker-proposals`;
    if (site === 'partner') {
      return await this.partnerApiService.get<PreferredWorkerProposalsDto>({
        path,
        params,
      });
    }
    throw new Error('Not implemented');
  }

  public async acceptPreferredWorkerProposal(site: Site, proposalId: string) {
    const path = `shift-planning/accept-preferred-worker-proposal/${proposalId}`;
    if (site === 'partner') {
      return await this.partnerApiService.update<void>({
        path,
      });
    }
    throw new Error('Not implemented');
  }
  public async rejectPreferredWorkerProposal(site: Site, proposalId: string) {
    const path = `shift-planning/reject-preferred-worker-proposal/${proposalId}`;
    if (site === 'partner') {
      return await this.partnerApiService.update<void>({
        path,
      });
    }
    throw new Error('Not implemented');
  }

  public async getShiftsPartners(
    site: Site,
    shiftIds: string[]
  ): Promise<ShiftPlanningShiftsPartner[]> {
    if (site === 'hq') {
      return await this.hqApiService.create<ShiftPlanningShiftsPartner[]>({
        path: 'shift-planning/get-shifts-partners',
        body: { shiftIds },
      });
    }
    if (site === 'ogp') {
      return await this.ogpApiService.create<ShiftPlanningShiftsPartner[]>({
        path: 'shifts/get-shifts-partners',
        body: { shiftIds },
      });
    }
    throw new Error('Not implemented for Partner sites');
  }

  public async bulkAssignPartnersToShifts(
    site: Site,
    shiftIds: string[],
    partnerIds: string[]
  ): Promise<BulkShiftActionData> {
    const response = await this.bulkAssignPartnersToShiftsImpl(site, shiftIds, partnerIds);
    return bulkShiftActionDataSchema.parse(response);
  }

  private async bulkAssignPartnersToShiftsImpl(
    site: Site,
    shiftIds: string[],
    partnerIds: string[]
  ): Promise<BulkShiftActionDataResponseDto> {
    if (site === 'hq') {
      return await this.hqApiService.update<BulkShiftActionDataResponseDto>({
        path: 'shift-planning/bulk-assign-partners-to-shifts',
        body: { shiftIds, partnerIds },
      });
    }
    if (site === 'ogp') {
      return await this.ogpApiService.update<BulkShiftActionDataResponseDto>({
        path: 'shifts/bulk-assign-partners-to-shifts',
        body: { shiftIds, partnerIds },
      });
    }
    throw new Error('Not implemented for Partner sites');
  }
}
